import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { GLOBAL_COUNTRIES as COUNTRIES, CountrySelect, inputCls } from '../family/shared';
import { pollJob } from '../../utils/aiJobs';

const flag = (c) => COUNTRIES.find((x) => x.code === c)?.flag;

export const CountryCompare = () => {
  const { api } = useAuth();
  const [f, setF] = useState({ topic: '', country_a: 'chile', country_b: 'argentina' });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.country_a === f.country_b) return toast.error('Elige dos países distintos');
    setLoading(true);
    try {
      const { data } = await api.post('/legislative/legal-order/compare', f);
      setRes(data.result || await pollJob(api, data.job_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'No se pudo comparar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="country-compare">
      <form onSubmit={submit} className="grid gap-3 rounded-2xl border border-slate-800 bg-slate-900/50 p-4 md:grid-cols-[1fr_180px_auto_180px_auto] md:items-end">
        <label className="block"><span className="mb-1 block text-xs uppercase tracking-wider text-slate-400">Tema jurídico</span>
          <input data-testid="compare-topic" required minLength={3} value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} placeholder="Ej: divorcio, despido injustificado, herencia, arriendo…" className={inputCls} /></label>
        <CountrySelect value={f.country_a} onChange={(v) => setF({ ...f, country_a: v })} testId="compare-country-a" options={COUNTRIES} />
        <button type="button" data-testid="compare-swap" onClick={() => setF({ ...f, country_a: f.country_b, country_b: f.country_a })} className="justify-self-center rounded-full p-2 text-slate-400 hover:bg-slate-800 hover:text-white"><ArrowLeftRight className="h-4 w-4" /></button>
        <CountrySelect value={f.country_b} onChange={(v) => setF({ ...f, country_b: v })} testId="compare-country-b" options={COUNTRIES} />
        <button data-testid="compare-submit" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Comparar
        </button>
      </form>
      {res && (
        <div data-testid="compare-result" className="space-y-4">
          <p className="text-white">{res.resumen}</p>
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-400">
                <tr><th className="p-3">Aspecto</th><th className="p-3">{flag(res.country_a)} {res.name_a}</th><th className="p-3">{flag(res.country_b)} {res.name_b}</th><th className="p-3">Diferencia</th></tr>
              </thead>
              <tbody>
                {(res.filas || []).map((r, i) => (
                  <tr key={i} data-testid={`compare-row-${i}`} className="border-t border-slate-800 align-top text-slate-200">
                    <td className="p-3 font-medium text-white">{r.aspecto}</td>
                    <td className="p-3"><p className="text-xs text-amber-300">{r.a?.norma}</p>{r.a?.regla}</td>
                    <td className="p-3"><p className="text-xs text-amber-300">{r.b?.norma}</p>{r.b?.regla}</td>
                    <td className="p-3 text-slate-400">{r.diferencia}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-300"><p className="mb-1 text-xs font-semibold uppercase text-blue-300">Ley aplicable</p>{res.ley_aplicable}</div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4 text-sm text-slate-300"><p className="mb-1 text-xs font-semibold uppercase text-blue-300">Reconocimiento entre países</p>{res.reconocimiento}</div>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-300">{(res.recomendaciones || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          <p className="text-[11px] text-slate-500">{res.vigencia}</p>
        </div>
      )}
    </div>
  );
};
